import React from 'react'
import { Box } from '@mui/material'

import type { HolidayImage } from '@/src/types/holidays'

import HolidayImageView from '../holidayImage'

interface HeroCarouselProps {
    images: HolidayImage[]
    activeIndex: number
    openGalleryLabel: string
    onScroll: (event: React.UIEvent<HTMLDivElement>) => void
    onOpenAt: (index: number) => void
    scrollerRef: React.Ref<HTMLDivElement>
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({
    images,
    activeIndex,
    openGalleryLabel,
    onScroll,
    onOpenAt,
    scrollerRef
}) => {
    return (
        <Box sx={{ position: 'relative' }}>
            {/*
             * A native scroll-snap track rather than a carousel library: it gives real momentum
             * swiping, costs no dependency, and degrades to a plain scroller if JS is slow.
             */}
            <Box
                ref={scrollerRef}
                onScroll={onScroll}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    bgcolor: 'neutral.100'
                }}
            >
                {images.map((image, index) => (
                    <Box
                        key={image.url}
                        component="button"
                        type="button"
                        onClick={() => onOpenAt(index)}
                        aria-label={openGalleryLabel}
                        sx={{
                            flex: '0 0 100%',
                            scrollSnapAlign: 'center',
                            aspectRatio: '1 / 1',
                            p: 0,
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            overflow: 'hidden'
                        }}
                    >
                        <HolidayImageView image={image} lazy={index > 0} isMobile />
                    </Box>
                ))}
            </Box>

            {images.length > 1 && (
                <Box
                    sx={{
                        position: 'absolute',
                        // Clears the content sheet, which overlaps the hero's bottom edge by 25px.
                        bottom: 40,
                        insetInlineStart: 0,
                        insetInlineEnd: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 0.75,
                        pointerEvents: 'none'
                    }}
                >
                    {images.map((image, index) => (
                        <Box
                            key={image.url}
                            sx={{
                                width: index === activeIndex ? 9 : 6,
                                height: index === activeIndex ? 9 : 6,
                                borderRadius: '50%',
                                bgcolor: 'neutral.white',
                                opacity: index === activeIndex ? 1 : 0.6,
                                transition: 'width 0.2s, height 0.2s, opacity 0.2s'
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    )
}

export { HeroCarousel }
export type { HeroCarouselProps }
export default HeroCarousel
